import { useState, useEffect, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { ReportType } from '../../../pages/PortfolioTable/types';
import { ReportParams, TabType, SelectedPortfolioInfo } from './types';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';
import { useReportProgressSSE } from './useReportProgressSSE';
import { useReportProgress } from '../../../stores/reportProgressStore';

interface UseReportGenerationModalParams {
  onSubmit: (params: ReportParams) => Promise<void>;
  onClose: () => void;
  selectedPortfolios?: SelectedPortfolioInfo[];
}

interface UseReportGenerationModalReturn {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  reportType: ReportType;
  setReportType: (type: ReportType) => void;
  fromDate: Dayjs | null;
  setFromDate: (date: Dayjs | null) => void;
  toDate: Dayjs | null;
  setToDate: (date: Dayjs | null) => void;
  isInception: boolean;
  setIsInception: (value: boolean) => void;
  isLoading: boolean;
  isFromDateDisabled: boolean;
  filenameTemplate: string;
  processed: number;
  total: number;
  success: number;
  failed: number;
  setFilenameTemplate: (template: string) => void;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;
}

export const useReportGenerationModal = ({
  onSubmit,
  onClose,
  selectedPortfolios,
}: UseReportGenerationModalParams): UseReportGenerationModalReturn => {
  const { connect: connectSSE, disconnect: disconnectSSE } = useReportProgressSSE();
  const { processed, total, success, failed, reset: resetProgress } = useReportProgress();

  const [activeTab, setActiveTab] = useState<TabType>('holdings');
  const [reportType, setReportType] = useState<ReportType>('highlights');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
  const [isInception, setIsInception] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filenameTemplate, setFilenameTemplate] = useState<string>(
    '{portfolio_name}_{report_type}_{from_date}_{to_date}'
  );

  // Calculate earliest inception date from selected portfolios
  const earliestInceptionDate = useMemo(() => {
    if (!selectedPortfolios?.length) return null;

    const dates = selectedPortfolios
      .map(p => p.inception_date)
      .filter((date): date is string => date !== null);

    if (dates.length === 0) return null;

    // Return the earliest date
    return dates.sort()[0];
  }, [selectedPortfolios]);

  // Reset Since Inception when Holdings tab is active
  useEffect(() => {
    if (activeTab === 'holdings') {
      setIsInception(false);
    }
  }, [activeTab]);

  // Clear fromDate when Since Inception checkbox state changes
  useEffect(() => {
    setFromDate(null);
  }, [isInception]);

  const handleClose = (): void => {
    setActiveTab('holdings');
    setReportType('highlights');
    setFromDate(null);
    setToDate(dayjs());
    setIsInception(false);
    setFilenameTemplate('{portfolio_name}_{report_type}_{from_date}_{to_date}');
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    // Determine the actual report type based on active tab
    const actualReportType = activeTab === 'holdings' ? 'holdings' : reportType;

    // Validation: Check if inception is selected but no portfolios have inception dates
    if (isInception && !earliestInceptionDate) {
      showSnackbar({
        title: 'Validation Error',
        message: 'No inception dates available for selected portfolios',
        type: SnackbarType.warning,
      });
      return;
    }

    // Validation: For non-Holdings reports not using inception, require at least one date
    if (
      actualReportType !== 'holdings' &&
      !isInception &&
      !fromDate &&
      !toDate
    ) {
      showSnackbar({
        title: 'Validation Error',
        message: "Please select at least one date or enable 'Since Inception'",
        type: SnackbarType.warning,
      });
      return;
    }

    try {
      resetProgress();
      connectSSE();
      setIsLoading(true);

      const params: ReportParams = {
        reportType: actualReportType,
        isInception,
        filenameTemplate,
      };

      // Only send fromDate if NOT using inception (backend will use per-portfolio inception dates)
      if (!isInception && fromDate) {
        params.fromDate = fromDate.format('YYYY-MM-DD');
      }

      if (toDate) {
        params.toDate = toDate.format('YYYY-MM-DD');
      }

      await onSubmit(params);
      handleClose();
    } catch (error) {
      console.error('Failed to generate report:', error);
      // Error is already shown by the hook's snackbar
      // Keep modal open so user can retry
    } finally {
      setIsLoading(false);
      disconnectSSE();
    }
  };

  const isHoldings = activeTab === 'holdings';
  const isFromDateDisabled = isInception;

  return {
    activeTab,
    setActiveTab,
    reportType,
    setReportType,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    isInception,
    setIsInception,
    isLoading,
    isHoldings,
    isFromDateDisabled,
    filenameTemplate,
    setFilenameTemplate,
    handleSubmit,
    handleClose,
    processed,
    total,
    success,
    failed,
  };
};
